import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Layout from '@/components/Layout'
import { fetchBusinesses, fetchEmirates, deleteBusiness } from '@/lib/api'
import type { Business, Emirate } from '@/lib/types'

const PLAN_COLORS: Record<string, string> = {
  premium: 'bg-amber-50 text-amber-700 border-amber-200',
  basic: 'bg-blue-50 text-blue-700 border-blue-200',
  free: 'bg-slate-50 text-slate-600 border-slate-200',
}

export default function BusinessesList() {
  const [businesses, setBusinesses] = useState<Business[]>([])
  const [emirates, setEmirates] = useState<Emirate[]>([])
  const [count, setCount] = useState(0)
  const [search, setSearch] = useState('')
  const [emirateFilter, setEmirateFilter] = useState('')
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const pageSize = 25

  useEffect(() => { fetchEmirates().then(setEmirates) }, [])

  useEffect(() => {
    setLoading(true)
    fetchBusinesses({ search: search || undefined, emirate_id: emirateFilter || undefined, page, pageSize })
      .then(res => { setBusinesses(res.data); setCount(res.count); setLoading(false) })
  }, [search, emirateFilter, page])

  async function handleDelete(id: string, name: string) {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return
    await deleteBusiness(id)
    setBusinesses(prev => prev.filter(b => b.id !== id))
    setCount(prev => prev - 1)
  }

  const totalPages = Math.ceil(count / pageSize)

  const action = (
    <Link to="/businesses/new" className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-all duration-200 shadow-lg shadow-brand-500/20 hover:shadow-brand-500/30">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
      Add Business
    </Link>
  )

  return (
    <Layout title="Businesses" action={action}>
      <div className="max-w-7xl mx-auto space-y-5">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.3-4.3"/></svg>
            <input
              type="text" placeholder="Search by name, phone, address..."
              value={search} onChange={e => { setSearch(e.target.value); setPage(1) }}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-surface-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all placeholder:text-slate-400"
            />
          </div>
          <select value={emirateFilter} onChange={e => { setEmirateFilter(e.target.value); setPage(1) }}
            className="px-4 py-2.5 rounded-xl border border-surface-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 text-slate-600">
            <option value="">All Emirates</option>
            {emirates.map(e => <option key={e.id} value={e.id}>{e.name_en}</option>)}
          </select>
        </div>

        {/* Results count */}
        <div className="flex items-center justify-between">
          <p className="text-[13px] text-slate-500 font-medium">
            {loading ? 'Loading...' : `${count.toLocaleString()} business${count !== 1 ? 'es' : ''} found`}
          </p>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-surface-200 shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-50/50">
                  <th className="text-left px-5 py-3.5 font-semibold text-[11px] text-slate-500 uppercase tracking-wider">Business</th>
                  <th className="text-left px-5 py-3.5 font-semibold text-[11px] text-slate-500 uppercase tracking-wider hidden md:table-cell">Emirate</th>
                  <th className="text-left px-5 py-3.5 font-semibold text-[11px] text-slate-500 uppercase tracking-wider hidden lg:table-cell">Contact</th>
                  <th className="text-center px-5 py-3.5 font-semibold text-[11px] text-slate-500 uppercase tracking-wider hidden md:table-cell">Rating</th>
                  <th className="text-center px-5 py-3.5 font-semibold text-[11px] text-slate-500 uppercase tracking-wider hidden lg:table-cell">Plan</th>
                  <th className="text-center px-5 py-3.5 font-semibold text-[11px] text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-right px-5 py-3.5 font-semibold text-[11px] text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-100">
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-5 py-4"><div className="h-4 w-48 bg-surface-100 rounded" /><div className="h-3 w-32 bg-surface-50 rounded mt-2" /></td>
                      <td className="px-5 py-4 hidden md:table-cell"><div className="h-4 w-20 bg-surface-100 rounded" /></td>
                      <td className="px-5 py-4 hidden lg:table-cell"><div className="h-4 w-28 bg-surface-100 rounded" /></td>
                      <td className="px-5 py-4 hidden md:table-cell"><div className="h-4 w-12 bg-surface-100 rounded mx-auto" /></td>
                      <td className="px-5 py-4 hidden lg:table-cell"><div className="h-4 w-16 bg-surface-100 rounded mx-auto" /></td>
                      <td className="px-5 py-4"><div className="h-4 w-14 bg-surface-100 rounded mx-auto" /></td>
                      <td className="px-5 py-4"><div className="h-4 w-20 bg-surface-100 rounded ml-auto" /></td>
                    </tr>
                  ))
                ) : businesses.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-16 text-center">
                      <div className="w-12 h-12 rounded-xl bg-surface-100 flex items-center justify-center mx-auto mb-3">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-slate-400"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.3-4.3"/></svg>
                      </div>
                      <p className="text-sm font-medium text-slate-600">No businesses found</p>
                      <p className="text-[13px] text-slate-400 mt-1">Try adjusting your search or filters</p>
                    </td>
                  </tr>
                ) : (
                  businesses.map(b => (
                    <tr key={b.id} className="hover:bg-surface-50/50 transition-colors group">
                      <td className="px-5 py-4">
                        <div>
                          <Link to={`/businesses/${b.id}`} className="text-sm font-semibold text-slate-900 hover:text-brand-600 transition-colors">
                            {b.name_en}
                          </Link>
                          {b.name_ar && <p className="text-[11px] text-slate-400 mt-0.5 font-arabic">{b.name_ar}</p>}
                        </div>
                      </td>
                      <td className="px-5 py-4 hidden md:table-cell">
                        <span className="inline-flex items-center gap-1.5 text-[13px] text-slate-600 font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-brand-400" />
                          {b.emirate?.name_en ?? ''}
                        </span>
                      </td>
                      <td className="px-5 py-4 hidden lg:table-cell">
                        <span className="text-[13px] text-slate-600 font-medium">{b.phone || '—'}</span>
                      </td>
                      <td className="px-5 py-4 text-center hidden md:table-cell">
                        {b.rating != null ? (
                          <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-amber-600">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" className="text-amber-400"><path d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"/></svg>
                            {b.rating.toFixed(1)}
                          </span>
                        ) : (
                          <span className="text-[13px] text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-center hidden lg:table-cell">
                        <span className={`inline-flex text-[11px] font-semibold px-2.5 py-1 rounded-full border ${PLAN_COLORS[b.plan] || PLAN_COLORS.free}`}>
                          {b.plan}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-center">
                        <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${b.is_active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${b.is_active ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                          {b.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Link to={`/businesses/${b.id}`} className="inline-flex items-center gap-1 text-[12px] font-medium text-slate-500 hover:text-brand-600 bg-surface-100 hover:bg-brand-50 px-3 py-1.5 rounded-lg transition-colors">
                            Edit
                          </Link>
                          <button onClick={() => handleDelete(b.id, b.name_en)} className="inline-flex items-center gap-1 text-[12px] font-medium text-slate-500 hover:text-red-600 bg-surface-100 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors">
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-5 py-4 border-t border-surface-200 bg-surface-50/30">
              <p className="text-[12px] text-slate-500 font-medium">
                Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, count)} of {count.toLocaleString()}
              </p>
              <div className="flex items-center gap-1.5">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="px-3 py-1.5 text-[12px] font-semibold rounded-lg border border-surface-200 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-surface-100 transition-colors text-slate-600">
                  Prev
                </button>
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  const start = Math.max(1, Math.min(page - 3, totalPages - 6))
                  const n = start + i
                  if (n > totalPages) return null
                  return (
                    <button key={n} onClick={() => setPage(n)}
                      className={`w-8 h-8 text-[12px] font-semibold rounded-lg transition-all ${
                        n === page ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/20' : 'border border-surface-200 text-slate-600 hover:bg-surface-100'
                      }`}>
                      {n}
                    </button>
                  )
                })}
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="px-3 py-1.5 text-[12px] font-semibold rounded-lg border border-surface-200 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-surface-100 transition-colors text-slate-600">
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  )
}
